import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "src/ui/shared/CustomButton";
import { Modal } from "src/ui/shared/Modal/Modal";
import { makeStyles } from "tss-react/mui";

import { Device } from "@dashboard/devices/types";
import TabContext from "@mui/lab/TabContext";
import MuiTabPanel from "@mui/lab/TabPanel";
import { Box, styled, Typography, useTheme } from "@mui/material";
import MuiAppBar from "@mui/material/AppBar";
import MuiTab from "@mui/material/Tab";
import MuiTabs, { TabsProps } from "@mui/material/Tabs";

import { ComplexCommands } from "./Tabs/ComplexCommands";
import { Examples } from "./Tabs/Examples";
import { JsonEditor } from "./Tabs/JsonEditor";

const AppBar = styled(MuiAppBar)({
    boxShadow: "none",
});
const Tabs = styled(MuiTabs)(({ theme }) => ({
    minHeight: theme.spacing(4),
    "& .MuiTabs-indicator": {
        backgroundColor: theme.palette.emteria.action,
    },
}));
const Tab = styled(MuiTab)(({ theme }) => ({
    minWidth: "200px",
    textTransform: "capitalize",
    fontWeight: 600,
    color: theme.palette.emteria.action,
    minHeight: theme.spacing(4),
}));
const TabPanel = styled(MuiTabPanel)({
    padding: 0,
});

const useStyles = makeStyles()({
    scrollBtn: {
        width: "20px",
        transition: "width 1s",
        "&.Mui-disabled": {
            width: 0,
        },
    },
    tabsContent: {
        maxHeight: "calc(100vh - 300px)",
        maxWidth: "calc(100vh - 300px)",
        overflowY: "auto",
    },
});

type Props = {
    title: string;
    devices: Device[];
    isOpen: boolean;
    toggleDialog: VoidFunction;
    isPolicy?: boolean;
    createCommand?: (json: Record<string, unknown>, commandId: number | null) => Promise<void>;
    loading?: boolean;
};

export type FormValues = {
    commandType: string;
};

export const NewCommandDialog = (props: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["common", "modals"]);
    const theme = useTheme();

    const submitRef = useRef<HTMLButtonElement>(null);
    const [tabValue, setTabValue] = useState(0);
    const editorRef = React.useRef<HTMLDivElement>(null);

    const { title, toggleDialog, isOpen, createCommand, loading } = props;

    const handleChange: TabsProps["onChange"] = (_event, value) => {
        if (typeof value === "number") {
            setTabValue(value);
        }
    };

    const a11yProps = (index: number) => {
        return {
            id: `details-tab-${index}`,
            "aria-controls": `details-tabpanel-${index}`,
        };
    };

    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if (event.key === "Enter") {
                submitRef?.current?.click();
            }
        };

        document.addEventListener("keydown", handleKeyPress);
        return () => {
            document.removeEventListener("keydown", handleKeyPress);
        };
    }, []);

    if (!isOpen) {
        return null;
    }

    return (
        <Modal
            open
            title={title}
            onClose={toggleDialog}
            endButton={
                <PrimaryButton onClick={() => submitRef?.current?.click()} type="submit" loading={loading} disabled={loading}>
                    {t("common:button.create")}
                </PrimaryButton>
            }
        >
            <TabContext value={String(tabValue)}>
                <AppBar position="static" color="default">
                    <Tabs
                        value={tabValue}
                        onChange={handleChange}
                        aria-label={t("common:label.details")}
                        scrollButtons="auto"
                        TabScrollButtonProps={{ classes: { root: classes.scrollBtn } }}
                    >
                        <Tab
                            label={
                                <Box style={{ display: "flex", alignItems: "center" }}>
                                    <i className="fa-solid fa-table-cells-large" style={{ marginRight: theme.spacing(2) }} />

                                    <Typography style={{ fontWeight: "bold", fontSize: 14 }}>
                                        {t("modals:newPolicyCommand.tab.examples")}
                                    </Typography>
                                </Box>
                            }
                            key={0}
                            {...a11yProps(0)}
                        />
                        <Tab
                            label={
                                <Box style={{ display: "flex", alignItems: "center" }}>
                                    <i className="fa-solid fa-wand-magic-sparkles" style={{ marginRight: theme.spacing(2) }} />

                                    <Typography style={{ fontWeight: "bold", fontSize: 14 }}>
                                        {t("modals:newPolicyCommand.tab.commands")}
                                    </Typography>
                                </Box>
                            }
                            key={1}
                            {...a11yProps(1)}
                        />
                        <Tab
                            label={
                                <Box style={{ display: "flex", alignItems: "center" }}>
                                    <i className="fa-solid fa-fw fa-pencil" style={{ marginRight: theme.spacing(2) }} />
                                    <Typography style={{ fontWeight: "bold", fontSize: 14 }}>
                                        {t("modals:newPolicyCommand.tab.editor")}
                                    </Typography>
                                </Box>
                            }
                            key={2}
                            {...a11yProps(2)}
                        />
                    </Tabs>
                </AppBar>
                <Box className={classes.tabsContent} style={{ padding: theme.spacing(4) }}>
                    <TabPanel value="0" key={0}>
                        <Examples
                            {...props}
                            editorRef={editorRef}
                            submitRef={submitRef}
                            toggleDialog={toggleDialog}
                            createCommand={createCommand}
                        />
                    </TabPanel>
                    <TabPanel value="1" key={1}>
                        <ComplexCommands
                            {...props}
                            editorRef={editorRef}
                            submitRef={submitRef}
                            toggleDialog={toggleDialog}
                            createCommand={createCommand}
                        />
                    </TabPanel>
                    <TabPanel value="2" key={2}>
                        <JsonEditor
                            {...props}
                            editorRef={editorRef}
                            submitRef={submitRef}
                            toggleDialog={toggleDialog}
                            createCommand={createCommand}
                        />
                    </TabPanel>
                </Box>
            </TabContext>
        </Modal>
    );
};
